import React from 'react';
import styled from 'styled-components';

import { useAuth } from '../../../theaWeb/firebase/FirebaseContext';
import { useIsSignedIn, type AuthOverride } from '../../../theaWeb/hooks/useIsSignedIn';
import { FOOTER_GUIDE_SECTIONS, footerGuideHref } from './footerGuideLinks';

export interface FooterProps {
  copyright?: string;
  privacyHref?: string;
  termsHref?: string;
  /**
   * Story/test/prerender override for the auth read that gates the guide
   * directory — same contract as LandingPage's. The static homepage render
   * (src/prerender/renderRoute.tsx) passes 'signed-out': effects never run
   * there, so without it `ready` would stay false and the one render crawlers
   * read would ship no guide links, which is the directory's entire purpose.
   */
  authOverride?: AuthOverride;
}

const Root = styled.footer`
  width: 100%;
  padding: 32px 0 calc(32px + env(safe-area-inset-bottom));
  @media (max-width: 1023px) { padding-bottom: calc(96px + env(safe-area-inset-bottom)); }
  border-top: 1px solid hsl(var(--border) / 0.4);
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: hsl(var(--muted-foreground));
  @media (min-width: 1024px) { padding: 0 32px; }
`;

const FooterLink = styled.a`
  padding: 8px 0;
  color: inherit;
  text-decoration: none;
  transition: color 200ms ease;
  &:hover {
    color: hsl(var(--foreground));
  }
`;

const Dot = styled.span`
  user-select: none;
`;

/* Signed-out only: an Airbnb-style directory of crawlable paths into the
 * /guide/* surface (see footerGuideLinks.ts). Plain anchors like the legal
 * links below — the footer renders outside any one route's tree and a full
 * navigation into a guide is fine, while a crawler needs a real href either
 * way. */
const GuideNav = styled.nav`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px 24px;
  font-size: 14px;
  color: hsl(var(--muted-foreground));
  @media (min-width: 1024px) { padding: 0 32px 28px; }
`;

const GuideNavTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 16px;
`;

const GuideHeading = styled.h2`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: hsl(var(--foreground) / 0.85);
`;

const AllGuidesLink = styled.a`
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 3px;
  transition: color 200ms ease;
  &:hover {
    color: hsl(var(--foreground));
  }
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px 16px;
  @media (min-width: 640px) { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  @media (min-width: 1024px) { grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 24px; }
`;

const SectionHeading = styled.h3`
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: hsl(var(--foreground) / 0.75);
`;

const SectionList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionLink = styled.a`
  color: inherit;
  text-decoration: none;
  transition: color 200ms ease;
  &:hover {
    color: hsl(var(--foreground));
  }
`;

const LegalRow = styled(Inner)`
  border-top: 1px solid hsl(var(--border) / 0.3);
  padding-top: 20px;
`;

/**
 * Signed-out visitors (and crawlers, which are always signed out) get the
 * gift-guide directory above the legal row; signed-in users keep the slim
 * footer. The gate waits for `ready` so a signed-in session never flashes
 * the directory during auth bootstrap.
 */
export const Footer: React.FC<FooterProps> = ({
  copyright = '© 2026 Thea',
  privacyHref = '/privacy',
  termsHref = '/terms',
  authOverride,
}) => {
  const auth = useAuth();
  const { ready, signedIn } = useIsSignedIn(authOverride, auth);
  const showGuides = ready && !signedIn;

  return (
    <Root>
      {showGuides && (
        <GuideNav aria-label="Gift guides">
          <GuideNavTop>
            <GuideHeading>Gift guides</GuideHeading>
            <AllGuidesLink href="/gift-guides/">All gift guides</AllGuidesLink>
          </GuideNavTop>
          <SectionGrid>
            {FOOTER_GUIDE_SECTIONS.map(({ heading, links }) => (
              <div key={heading}>
                <SectionHeading>{heading}</SectionHeading>
                <SectionList>
                  {links.map(({ slug, title }) => (
                    <li key={slug}>
                      <SectionLink href={footerGuideHref(slug)}>{title}</SectionLink>
                    </li>
                  ))}
                </SectionList>
              </div>
            ))}
          </SectionGrid>
        </GuideNav>
      )}
      {showGuides ? (
        <LegalRow>
          <span>{copyright}</span>
          <Dot>·</Dot>
          <FooterLink href={privacyHref}>Privacy Policy</FooterLink>
          <Dot>·</Dot>
          <FooterLink href={termsHref}>Terms of Use</FooterLink>
        </LegalRow>
      ) : (
        <Inner>
          <span>{copyright}</span>
          <Dot>·</Dot>
          <FooterLink href={privacyHref}>Privacy Policy</FooterLink>
          <Dot>·</Dot>
          <FooterLink href={termsHref}>Terms of Use</FooterLink>
        </Inner>
      )}
    </Root>
  );
};
